"use client";

import { useEffect, useRef, useState } from "react";
import { startFexprimeKyc } from "@/lib/fexprimeKyc"
import {
  startGromorKyc,
  startGromorSelfie,
  pollGromorKycUntilComplete,
  fetchGromorKycStatus,
} from "@/lib/gromorKyc";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight,
  ExternalLink,
  RefreshCw,
  Frown,
  CircleCheck,
  Timer,
} from "lucide-react";
import { handleAndTrackError } from "@/lib/helper";
import { fetchData } from "@/lib/helper";
import { DISBURSEMENT_DATE_PASSED } from "@/lib/disbursementDatePassed";
import {
  isEsignStartRefused,
  esignStartRefusalMessage,
} from "@/b2b/lib/esignStartRefusal";
import SupportRequestDialog from "@/components/common/SupportRequestDialog";
import PendingBanking from "@/assets/icons/banking/emandate/pending.svg";
import PendingKyc from "@/assets/icons/banking/kyc/pending.svg";
import PendingEsign from "@/assets/icons/banking/esign/pending.svg";
import RejectedKyc from "@/assets/icons/banking/kyc/rejected.svg";
import RejectedBanking from "@/assets/icons/banking/emandate/rejected.svg";
import RejectedESign from "@/assets/icons/banking/esign/rejected.svg";

import LockedBankingHousr from "@/b2b/assets/housr/locked-banking-housr.svg";
import LockedEsignHousr from "@/b2b/assets/housr/locked-esign-housr.svg";
import PendingKycHousr from "@/b2b/assets/housr/pending-kyc-housr.svg";
import PendingBankingHousr from "@/b2b/assets/housr/pending-banking-housr.svg";
import PendingEsignHousr from "@/b2b/assets/housr/pending-esign-housr.svg";
import config from "@/config";

const iconConfig = {
  housr: {
    lockedBanking: LockedBankingHousr,
    lockedEsign: LockedEsignHousr,
    pendingKyc: PendingKycHousr,
    pendingBanking: PendingBankingHousr,
    pendingEsign: PendingEsignHousr,
  },
  xu: {
    lockedBanking: LockedBankingHousr,
    lockedEsign: LockedEsignHousr,
    pendingKyc: PendingKycHousr,
    pendingBanking: PendingBankingHousr,
    pendingEsign: PendingEsignHousr,
  },
};

const GROMOR_KYC_IDLE = { preparing: false, url: null, opened: false, stuck: false, error: null };
// Longer than cpcore's slowest honest answer; past this the tenant gets "Start" back.
const GROMOR_KYC_PREPARE_TIMEOUT_MS = 45_000;

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("This is taking too long.")), ms),
    ),
  ]);

export default function OnboardingStepsCard({
  data,
  theme,
  setIsLoading,
  lender,
}) {
  // The journey's lender, from the parent (OnboardingSteps already holds the journey). Every
  // Gromor-only behaviour below is keyed on it, so a FEXPRIME/FINTREE tenant sees exactly the
  // card they saw before Gromor existed — same calls, same states.
  const isGromorJourney = lender === "GROMOR";
  const navigate = useNavigate();

  const lockedKyc = PendingKyc;
  const lockedBanking = iconConfig[theme.name]?.lockedBanking || PendingBanking;
  const lockedEsign = iconConfig[theme.name]?.lockedEsign || PendingEsign;

  const pendingKyc = iconConfig[theme.name]?.pendingKyc || PendingKyc;
  const pendingBanking =
    iconConfig[theme.name]?.pendingBanking || PendingBanking;
  const pendingEsign = iconConfig[theme.name]?.pendingEsign || PendingEsign;

  const [isClicked, setIsClicked] = useState(false);
  // Gromor KYC only: the verification page is prepared here on the card, then offered as a link
  // (`url`); `opened` once the tenant has tapped it; `stuck` when the tab did not open (some
  // in-app browsers ignore new tabs); `error` is shown on the card when preparing failed.
  const [gromorKyc, setGromorKyc] = useState(GROMOR_KYC_IDLE);
  const gromorKycPreparing = useRef(false);

  /**
   * Catch up on a DigiLocker that finished while we were not watching.
   *
   * WHY THIS EXISTS: the first version only polled on a one-shot `focus` listener attached
   * when we opened the DigiLocker tab. That assumed the tenant finishes and clicks straight
   * back onto our tab. Observed in testing: they closed the DigiLocker tab without returning,
   * the listener never fired, and a KYC that Gromor had marked SUCCESS — with consent and full
   * Aadhaar data — sat unnoticed. Nothing would ever have asked again.
   *
   * Gromor sends NO KYC webhook, so a poll is the only way we ever learn. Polling whenever the
   * card mounts in a WAITING state covers every return path — same tab, new tab, fresh load,
   * or coming back tomorrow — instead of depending on one event firing once.
   *
   * Cheap and safe: it only runs for a Gromor journey whose KYC step is WAITING (i.e. we know
   * the tenant has a link and has gone off to use it), it is read-only against Gromor, and the
   * server short-circuits once KYC is already complete.
   */
  useEffect(() => {
    if (!isGromorJourney || data?.name !== "KYC" || data?.status !== "WAITING")
      return;

    let cancelled = false;

    const catchUp = async () => {
      try {
        const { journey } = await fetchData("journey/full");
        if (journey?.lender !== "GROMOR" || cancelled) return;

        const result = await fetchGromorKycStatus();
        if (result.complete && !cancelled) {
          navigate(0); // re-read the journey so the card renders its new status
        }
        // Never sends THIS tab to a vendor. The selfie and DigiLocker open in a new tab from
        // the tenant's tap; this page stays the hub and only polls.
      } catch (error) {
        // A failed catch-up is not a failed KYC. The tenant can still tap, and the reconcile
        // cron is the backstop — so this stays silent rather than showing them an error.
        console.error("Gromor KYC catch-up failed", error);
      }
    };

    catchUp();

    // Also when they come back to this tab, for the case where the card was already mounted.
    const onVisible = () => {
      if (document.visibilityState === "visible") catchUp();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [isGromorJourney, data?.name, data?.status]);
  const partner = localStorage.getItem("partnerIdentifier");
  const [supportDialog, setSupportDialog] = useState({
    open: false,
    topic: null,
    problemLine: "",
  });
  const openSupportDialog = (topic, problemLine) =>
    setSupportDialog({ open: true, topic, problemLine });

  let bankingUrl, esignUrl;

  if (theme) {
    bankingUrl = `/b/${localStorage.getItem("user_identifier")}?partner=${
      theme?.name
    }&step=banking`;
    esignUrl = `/b/${localStorage.getItem("user_identifier")}?partner=${
      theme?.name
    }&step=esign`;
  }

  const startESign = async () => {
    try {
      setIsLoading((prevState) => !prevState);
      // Gromor is clickwrap: there is no session to start. `esign/start-esign` would fall
      // into the native branch and mint a CirclePe loan id for a Gromor journey, so on
      // Gromor the card just opens the screen; the screen's own Next drives the flow.
      if (!isGromorJourney) {
        // fetchData returns the parsed body on 2xx (and undefined on a swallowed non-2xx). A
        // refused start is a 200 with `success: false` — the server either just moved the journey
        // back to the bank-statement flow or a required step is not done — so navigating to the
        // eSign screen regardless would land the tenant on a step they can no longer take.
        const res = await fetchData("esign/start-esign");
        // A past disbursement date opens the eSign screen as usual: its Next shows the notice.
        if (isEsignStartRefused(res) && res.message !== DISBURSEMENT_DATE_PASSED) {
          window.alert(esignStartRefusalMessage(res.message));
          // Refresh the step list. The parent (OnboardingSteps) owns the refetch — its mount
          // effect re-runs `b2b/onboard/start` → getData(...) which re-reads the journey and
          // routes to the right screen (banking if the journey was reverted) — but it is not
          // exposed to this card, so a reload is the way to re-run exactly that path.
          window.location.reload();
          return;
        }
      }
      navigate(esignUrl);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading((prevState) => !prevState);
    }
  };

  // Tap, then a real link. The tap prepares the verification page right here on the card
  // ("Preparing…"), then the button BECOMES a link that opens it in a new tab:
  //   Continue -> Digio selfie -> (cpcore /gromor-kyc/after-selfie relays it) -> Gromor DigiLocker
  //
  // We used to open a blank tab inside the tap and fill it once the link arrived: the tenant sat
  // on a white tab while the vendor answered and thought it had broken (production, 28 Sep). A
  // link the tenant taps is never blocked as a popup. This page stays the hub (DigiLocker ends on
  // Gromor's own page, which would strand the tenant): when they come back, it checks whether
  // KYC finished. If the selfie is already captured, the link goes straight to DigiLocker.
  const prepareGromorKyc = async () => {
    if (gromorKycPreparing.current) return;
    gromorKycPreparing.current = true;
    setGromorKyc({ ...GROMOR_KYC_IDLE, preparing: true });
    try {
      const url = await withTimeout(
        prepareGromorKycUrl(),
        GROMOR_KYC_PREPARE_TIMEOUT_MS,
      );
      if (url === null) return; // already complete: the page is reloading
      setGromorKyc({ ...GROMOR_KYC_IDLE, url });
    } catch (error) {
      handleAndTrackError(error, { apiPath: "/gromor-kyc" });
      setGromorKyc({
        ...GROMOR_KYC_IDLE,
        error: `Could not start verification (${error.message}). Please tap again.`,
      });
    } finally {
      gromorKycPreparing.current = false;
      setIsClicked(false);
    }
  };

  // The verification page's URL, or null when KYC turned out to be complete already.
  const prepareGromorKycUrl = async () => {
    const selfie = await startGromorSelfie();
    let url = !selfie.done && selfie.sessionUrl ? selfie.sessionUrl : null;
    if (!url) {
      const kyc = await startGromorKyc();
      if (kyc.complete) {
        // KYC is already done (this card was stale): re-read the journey so it shows finished.
        navigate(0);
        return null;
      }
      url = kyc.link;
    }
    if (!url) throw new Error("no verification link was returned");
    return url;
  };

  // Once the tenant has opened the verification tab: when they come back, see whether KYC
  // finished — Gromor sends no KYC webhook — and re-read the journey if it did. If it did not,
  // the used link is dropped and "Start" comes back: the next tap asks cpcore again, which hands
  // out a fresh selfie session or goes straight to DigiLocker once the selfie is captured.
  useEffect(() => {
    if (!gromorKyc.opened) return undefined;
    const onVisible = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const result = await fetchGromorKycStatus();
        if (result.complete) {
          navigate(0);
          return;
        }
      } catch (error) {
        // Not a failed KYC: the tenant can still tap, and the reconcile cron is the backstop.
        console.error("Gromor KYC check on return failed", error);
      }
      setGromorKyc(GROMOR_KYC_IDLE);
    };
    document.addEventListener("visibilitychange", onVisible);
    // Our page still showing 2 s after the tap means the tab did not open (some in-app browsers
    // ignore new tabs): offer to continue here instead.
    const stuckTimer = setTimeout(() => {
      if (document.visibilityState === "visible") {
        setGromorKyc((prev) => (prev.url ? { ...prev, stuck: true } : prev));
      }
    }, 2000);
    return () => {
      clearTimeout(stuckTimer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [gromorKyc.opened]);

  const clickKycAction = async () => {
    // Gromor: prepared on the card itself. The page-wide loader would unmount this card and lose
    // the link it is about to offer.
    if (isGromorJourney) {
      await prepareGromorKyc();
      return;
    }
    // A path that ends in a reload skips the toggle in `finally`.
    let loadingSettled = false;
    try {
      setIsLoading((prevState) => !prevState);

      const journeyResponse = await fetchData("journey/full");
      console.log("[v0] Journey response:", journeyResponse);

      const { journey } = journeyResponse;

      if (journey?.lender === "GROMOR") {
        // The journey moved to Gromor after this page loaded: re-read it, so this card takes
        // the Gromor path.
        loadingSettled = true;
        navigate(0);
        return;
      }
      if (journey?.lender === "FEXPRIME") {
        // CKYC first (when the rollout flag is on), then the mandatory eKYC.
        // Both cards share one implementation — see src/lib/fexprimeKyc.js for why
        // there are two endpoints and why unknown answers fail OPEN to the eKYC.
        await startFexprimeKyc({ navigate, partner })
      } else {
        setIsLoading((prevState) => !prevState);
        const response = await fetch(`${config.API_URL}/ekyc/start-kyc/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              config.LOCAL_KEYS.SIGNUP_TOKEN
            )}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData.message || "Failed to start KYC session.";
          const error = new Error(errorMessage);

          handleAndTrackError(error, {
            apiPath: "/ekyc/start-kyc/",
            responseStatus: response.status,
            responseError: errorData,
          });
        }

        const data = await response.json();
        console.log("KYC Response Data KIKI:", data);
        if (
          data?.event === "CKYC_SEARCH_SUCCESS" &&
          data?.data?.sessionUrl === null
        ) {
          // redirect to confirm ckyc page
          setIsLoading((prevState) => !prevState);
          navigate(`/kyc?partner=${partner}`, {
            state: {
              data: data?.data?.result[0] || data?.data?.result,
              kycRefId: data?.data?.refId,
            },
          });
          return;
        } else if (
          data?.event === "CKYC_SEARCH_SUCCESS" &&
          data?.data?.sessionUrl
        ) {
          // redirect to digio for now
          navigate(`/kyc?partner=${partner}`, {
            state: {
              data: data?.data?.result[0] || data?.data?.result,
              kycRefId: data?.data?.refId,
            },
          });
          return;
        } else if (
          data?.event === "FULL_KYC_INITIATED" ||
          data?.event === "CKYC_DOWNLOAD_SUCCESS"
        ) {
          // Both branches previously assigned data.data.sessionUrl to window.location.href
          // without a real guard. FULL_KYC_INITIATED had none at all, and
          // CKYC_DOWNLOAD_SUCCESS tested `!== null` — which LOOKS safe but lets `undefined`
          // straight through, since `undefined !== null` is true. Either one navigates the
          // tenant to a page literally named "undefined". A truthy check covers null,
          // undefined and "" in one, and a missing session URL is a real backend failure,
          // so it is tracked rather than silently swallowed.
          const sessionUrl = data?.data?.sessionUrl;
          if (!sessionUrl) {
            handleAndTrackError(
              new Error(`KYC session URL missing for event ${data?.event}`),
              { apiPath: "/ekyc/start-kyc/", kycEvent: data?.event },
            );
            setIsLoading((prevState) => !prevState);
            return;
          }
          window.location.href = sessionUrl;
          return;
        }
      }
    } catch (error) {
      handleAndTrackError(error, {
        apiPath: "journey/full",
      });
    } finally {
      if (!loadingSettled) setIsLoading((prevState) => !prevState);
    }
  };

  if (!data) return null;

  const cardConfig = {
    KYC: {
      heading: "Identity Verification",
      PENDING: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#0031CC",
          message: "Upload your PAN and Selfie to verify your identity",
          action: {
            text: "Start",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            onClick: () => {
              setIsClicked((prevState) => !prevState);
              clickKycAction();
            },
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#313131",
          message: "Upload your PAN and Selfie to verify your identity",
          showTimer: true,
          timeTaken: "30 secs",
        },
        icon: pendingKyc,
      },
      FAILED: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
          action: {
            text: "Retry",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => {
              setIsClicked((prevState) => !prevState);
              clickKycAction();
            },
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
          showTimer: true,
          timeTaken: "30 secs",
        },
        RETRY: {
          color: "#FA4D4D",
          message: null,
          action: {
            text: "Retry",
            className: `${
              !isClicked ? "bg-white" : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => {
              setIsClicked((prevState) => !prevState);
              clickKycAction();
            },
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
        },
        icon: RejectedKyc,
      },
      WAITING: {
        // WAITING + UNLOCKED means the tenant HAS a DigiLocker link and still has something to
        // do — they are mid-flow, not under review. Without this the card resolved to undefined
        // config and rendered blank with no button, stranding a tenant one tap from finishing.
        // (Same dead-card shape as RETRY and IN_REVIEW below.) Gromor journeys only: the copy
        // and the Continue action are Gromor's flow, and on another lender a Continue here would
        // restart that lender's KYC. Their card stays exactly as it was.
        UNLOCKED: isGromorJourney ? {
          color: theme.primaryButtonBgColorCode || "#0031CC",
          message: "Finish verifying your identity with DigiLocker",
          action: {
            text: "Continue",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            onClick: () => {
              setIsClicked((prevState) => !prevState);
              clickKycAction();
            },
          },
          showTimer: true,
          timeTaken: "2 mins",
        } : undefined,
        LOCKED: {
          color: "#FFE586",
          message: "Your KYC is under review. We will update you shortly.",
          headingTextColor: "#000000",
          action: {
            text: "Support",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: lockedKyc,
            onClick: () =>
              openSupportDialog(
                "kyc-stuck",
                "My KYC/identity verification is under review and I am still waiting.",
              ),
          },
        },
        icon: PendingKyc,
      },
      // Same bug BANKING.RETRY was added to fix (see the note on it below): a top-level
      // RETRY status had NO config here, so it resolved to undefined and rendered a dead grey
      // card with no button. The Gromor flow puts a tenant into RETRY on any recoverable
      // DigiLocker failure — a dropped signal, a UIDAI timeout — which is the common case, so
      // without this they would be stranded on the screen they were closest to finishing.
      // Gromor journeys only, like WAITING+UNLOCKED above.
      RETRY: isGromorJourney ? {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message: "Verification didn't complete. Please try again.",
          action: {
            text: "Retry",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => {
              setIsClicked((prevState) => !prevState);
              clickKycAction();
            },
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Verification didn't complete. Please try again.",
          showTimer: true,
          timeTaken: "30 secs",
        },
        icon: RejectedKyc,
      } : undefined,
      // IN_REVIEW had no config either, and it is not a rare state on the Gromor flow: it is
      // where a tenant lands when DigiLocker succeeded but the selfie upload has not landed
      // yet. It clears itself when the reconcile door retries, so this is a waiting screen
      // with a support escape hatch — NOT a failure. Gromor journeys only: on other lenders
      // IN_REVIEW is a real manual review, which this "takes a moment" copy would misdescribe.
      IN_REVIEW: isGromorJourney ? {
        UNLOCKED: {
          color: "#FFE586",
          headingTextColor: "#000000",
          message: "We're finishing your verification. This usually takes a moment.",
          action: {
            text: "Support",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: lockedKyc,
            onClick: () =>
              openSupportDialog(
                "kyc-stuck",
                "My identity verification has been finishing for a while.",
              ),
          },
        },
        LOCKED: {
          color: "#FFE586",
          headingTextColor: "#000000",
          message: "We're finishing your verification. This usually takes a moment.",
        },
        icon: PendingKyc,
      } : undefined,
      REJECTED: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Upload your PAN and Selfie to verify your identity",
        },
        icon: RejectedKyc,
      },
    },
    BANKING: {
      heading: "Income Verification",
      PENDING: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#0031CC",
          message: "Connect your salary bank account to verify your income",
          action: {
            text: "Start",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            onClick: () => navigate(bankingUrl),
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#313131",
          message: "Connect your salary bank account to verify your income",
          showTimer: true,
          timeTaken: "30 secs",
        },
        icon: lockedBanking,
      },
      FAILED: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
          action: {
            text: "Retry",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => navigate(bankingUrl),
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
          showTimer: true,
          timeTaken: "30 secs",
        },
        RETRY: {
          color: "#FA4D4D",
          message: null,
          action: {
            text: "Retry",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw className="w-5 h-5 ml-2" />,
            showTimer: false,
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
        },
        icon: RejectedBanking,
      },
      // The backend writes STATUS "RETRY" / state "UNLOCKED" when a banking attempt fails
      // retryably (Digitap "Couldn't fetch data", salary not found, session expired). The
      // key above only covers state:"RETRY" nested under status:"FAILED" — a different
      // shape — so a real RETRY step resolved to undefined config and rendered a dead grey
      // card with NO retry button (546 open journeys sat in RETRY on 2026-08-10, and the
      // tenant's only way forward was an ops ticket). Mirrors FAILED above and the D2C
      // card's BANKING.RETRY (src/components/banking/StepCard.jsx).
      RETRY: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
          action: {
            text: "Retry",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => navigate(bankingUrl),
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
          showTimer: true,
          timeTaken: "30 secs",
        },
        icon: RejectedBanking,
      },
      WAITING: {
        LOCKED: {
          color: "#FFE586",
          message: "Upload your PAN and Selfie to verify your identity",
          headingTextColor: "#000000",
          action: {
            text: "Support",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: lockedBanking,
            onClick: () =>
              openSupportDialog(
                "banking-stuck",
                "My income/bank verification is stuck and I am still waiting.",
              ),
          },
        },
        icon: lockedBanking,
      },
      REJECTED: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
        },
        LOCKED: {
          color: "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
        },
        icon: RejectedBanking,
      },
    },
    E_SIGN: {
      heading: "E-Sign/Sanction",
      PENDING: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#0031CC",
          message: "Check your rent summary and sign via simple OTP",
          action: {
            text: "Start",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            onClick: () => startESign(),
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#313131",
          message: "Check your rent summary and sign via simple OTP",
          showTimer: true,
          timeTaken: "30 secs",
        },
        icon: lockedEsign,
      },
      FAILED: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message: "Check your rent summary and sign via simple OTP",
          action: {
            text: "Retry",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: <RefreshCw size={12} />,
            onClick: () => startESign(),
          },
          showTimer: true,
          timeTaken: "30 secs",
        },
        LOCKED: {
          color: "#FA4D4D",
          message: "Check your rent summary and sign via simple OTP",
          showTimer: true,
          timeTaken: "30 secs",
        },
        RETRY: {
          color: "#FA4D4D",
          message: "Check your rent summary and sign via simple OTP",
          action: {
            text: "Retry",
            className: "bg-white text-black flex items-center",
            icon: <RefreshCw className="w-5 h-5 ml-2" />,
            showTimer: false,
          },
          statusMessage: {
            text: (
              <>
                <Frown className="w-4 h-4 mr-2" />
                Couldn't Approve
              </>
            ),
            className: "border-red-700 text-red-700",
          },
        },
        icon: RejectedESign,
      },
      WAITING: {
        // Gromor: Next was tapped and the agreement is being generated (or is ready and
        // unread). Without this config the card rendered blank — the same dead-card bug
        // the KYC WAITING/UNLOCKED entry was added for. Gromor journeys only.
        UNLOCKED: isGromorJourney ? {
          color: "#FFE586",
          message: "Your loan agreement is being prepared. Continue to review and sign it.",
          headingTextColor: "#000000",
          action: {
            text: "Continue",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: (
              <div className="rounded-full bg-black">
                <ChevronRight color="#000" size={12} />
              </div>
            ),
            onClick: () => navigate(esignUrl),
          },
        } : undefined,
        LOCKED: {
          color: "#FFE586",
          message: "Upload your PAN and Selfie to verify your identity",
          headingTextColor: "#000000",
          action: {
            text: "Support",
            className: `${
              !isClicked ? `${theme?.primaryButtonBgColor}` : "bg-gray-500"
            } text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`,
            icon: lockedEsign,
            onClick: () =>
              openSupportDialog(
                "esign-stuck",
                "My e-sign/sanction step is stuck and I am still waiting.",
              ),
          },
        },
        icon: lockedEsign,
      },
      REJECTED: {
        UNLOCKED: {
          color: theme.primaryButtonBgColorCode || "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
        },
        LOCKED: {
          color: "#FA4D4D",
          message:
            "Please connect the bank account in which you get your monthly salary",
        },
        icon: RejectedESign,
      },
    },
  };

  const stepStatus = data.status;

  const headingBgColor =
    cardConfig[data.name]?.[data.status]?.[data.state]?.color || "#313131";
  const headingText = cardConfig[data.name]?.heading;
  const subText = cardConfig[data.name]?.[data.status]?.[data.state]?.message;
  const showTimer =
    cardConfig[data.name]?.[data.status]?.[data.state]?.showTimer;
  const timeRequired =
    cardConfig[data.name]?.[data.status]?.[data.state]?.timeTaken || "30 secs";
  const actionButton =
    cardConfig[data.name]?.[data.status]?.[data.state]?.action;
  const icon =
    cardConfig[data.name]?.[data.status]?.[data.state]?.icon ||
    cardConfig[data.name]?.[data.status]?.icon;
  const headingTextColor =
    cardConfig[data.name]?.[data.status]?.[data.state]?.headingTextColor ||
    "#eeeeee";

  const lockedActionButton =
    cardConfig[data.name]?.[data.status]?.[data.state]?.action;
  // Gromor KYC: once prepared, the action IS the link to the verification page.
  const gromorKycLink =
    isGromorJourney && data.name === "KYC" && lockedActionButton
      ? gromorKyc.url
      : null;

  const supportDialogNode = (
    <SupportRequestDialog
      open={supportDialog.open}
      onOpenChange={(open) => setSupportDialog((prev) => ({ ...prev, open }))}
      topic={supportDialog.topic}
      problemLine={supportDialog.problemLine}
    />
  );

  if (stepStatus === "SUCCESS" || stepStatus === "SUCESS") {
    return (
      <div className="w-full">
        <div className="flex justify-between border border-[#13C06DC2] text-[#12c06c] items-center p-4 text-sm rounded-md">
          <span>{headingText}</span>
          <div className="flex items-center text-2xs px-1 py-1 gap-2 rounded-full border border-[#12c06c]">
            <CircleCheck size={12} color="#12c06c" />
            Completed
          </div>
        </div>
        {supportDialogNode}
      </div>
    );
  } else {
    return (
      <div className="w-full">
        <div className="flex">
          <div className="w-[70%] flex items-center justify-center pr-2 pb-2 rounded-md">
            <div
              className="rounded-md w-full flex items-center justify-center p-2"
              style={{
                backgroundColor: headingBgColor,
                color: headingTextColor, // Apply the custom text color
              }}
            >
              {headingText}
            </div>
          </div>
          <div
            className="w-[30%] flex items-center justify-center pr-2 pb-2 rounded-t-md border-t border-l border-r"
            style={{ borderColor: headingBgColor }}
          >
            <img
              src={icon || "/placeholder.svg"}
              className="-mb-12 pt-4"
              alt=""
            />
          </div>
        </div>
        <div className="flex">
          <div
            className="flex flex-col gap-4 w-[70%] p-4 rounded-bl-md rounded-tl-md border-l border-t border-b"
            style={{ borderColor: headingBgColor }}
          >
            <div
              className={`w-full ${theme?.darkTextColor} text-2xs font-medium`}
            >
              {subText}
            </div>
            {gromorKycLink && (
              <div
                className={`w-full ${theme?.darkTextColor} text-3xs`}
                data-testid="gromor-kyc-ready"
              >
                Ready — tap Continue. It opens in a new tab; come back here when you finish.
                {gromorKyc.stuck && (
                  <>
                    {" "}
                    Didn&apos;t open?{" "}
                    <a
                      href={gromorKycLink}
                      className="underline"
                      data-testid="gromor-kyc-same-tab"
                    >
                      Continue here
                    </a>
                  </>
                )}
              </div>
            )}
            {isGromorJourney && data.name === "KYC" && gromorKyc.error && (
              <div className="w-full text-3xs text-red-600" data-testid="gromor-kyc-error">
                {gromorKyc.error}
              </div>
            )}
            {showTimer && (
              <div
                className={`w-fit px-2 py-[0.5px] border ${theme?.darkBorderColor} rounded-md flex items-center gap-2`}
              >
                <Timer size={12} />
                <span
                  className={`${theme?.darkTextColor} text-3xs font-medium`}
                >
                  {timeRequired}
                </span>
              </div>
            )}
          </div>
          <div
            className="flex flex-col justify-end items-center w-[30%] p-4 rounded-br-md border-b border-r"
            style={{ borderColor: headingBgColor }}
          >
            {gromorKycLink ? (
              // The prepared verification page: a real link, so the tab opens straight onto it.
              <a
                href={gromorKycLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setGromorKyc((prev) => ({ ...prev, opened: true }))}
                data-testid="gromor-kyc-continue"
                className={`${theme?.primaryButtonBgColor} text-white text-2xs px-2 py-0.5 rounded-md flex gap-1 items-center`}
              >
                Continue
                <ExternalLink size={12} />
              </a>
            ) : (
              lockedActionButton && (
                <button
                  onClick={lockedActionButton.onClick}
                  disabled={gromorKyc.preparing}
                  className={`${lockedActionButton.className}`}
                >
                  {gromorKyc.preparing ? "Preparing…" : lockedActionButton.text}
                </button>
              )
            )}
          </div>
        </div>
        {supportDialogNode}
      </div>
    );
  }
}
